import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Icon from '../../../Assets/Icon/CustomIcon';
import { groupRecipientsByDepartment } from './workStopUtils';

const EMPTY_ARRAY = [];

const sectionTitleSx = {
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#3A3A3A',
  marginBottom: '8px',
};

const accordionSx = {
  boxShadow: 'none',
  backgroundColor: '#fff',
  border: '1px solid #EFEFEF',
  borderRadius: '8px !important',
  '&:before': { display: 'none' },
  '&.Mui-expanded': {
    margin: '0 !important',
  },
  '& + &': {
    mt: 1,
  },
};

function RecipientMemberRow({ person }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        py: 0.75,
        borderBottom: '1px solid #F3F3F3',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box component="span" sx={{ fontSize: '13px', color: '#918A8A' }}>
        {person.position?.trim() || '-'}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#DD8B44',
          textAlign: 'right',
          wordBreak: 'break-all',
        }}
      >
        {person.name?.trim() || '-'}
      </Box>
    </Box>
  );
}

export default function WorkStopRecipientSection({
  receivingDepartments,
  recipients,
}) {
  const list = recipients ?? EMPTY_ARRAY;

  const recipientKey = useMemo(() => {
    const src = recipients ?? EMPTY_ARRAY;
    return src
      .map(
        (r) =>
          r.id ?? `${r.name ?? ''}-${r.depNmLast ?? ''}-${r.position ?? ''}`
      )
      .join('|');
  }, [recipients]);

  const departments = useMemo(
    () => (receivingDepartments ?? EMPTY_ARRAY).filter((d) => d?.depNmLast?.trim()),
    [receivingDepartments]
  );

  const grouped = useMemo(
    () => groupRecipientsByDepartment(recipients ?? EMPTY_ARRAY),
    [recipientKey]
  );

  const [expandedDeps, setExpandedDeps] = useState(new Set());

  useEffect(() => {
    if (grouped.length === 0) {
      setExpandedDeps((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }

    const firstDep = grouped[0].depNmLast;
    setExpandedDeps((prev) => {
      if (prev.size === 1 && prev.has(firstDep)) return prev;
      return new Set([firstDep]);
    });
  }, [recipientKey]);

  const isAllExpanded =
    grouped.length > 0 && expandedDeps.size === grouped.length;

  const handleToggleDep = (depNmLast) => (_event, isExpanded) => {
    setExpandedDeps((prev) => {
      const next = new Set(prev);
      if (isExpanded) next.add(depNmLast);
      else next.delete(depNmLast);
      return next;
    });
  };

  const handleToggleAll = () => {
    if (isAllExpanded) {
      setExpandedDeps(new Set());
    } else {
      setExpandedDeps(new Set(grouped.map((g) => g.depNmLast)));
    }
  };

  if (!departments.length && !list.length) return null;

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {departments.length > 0 && (
        <Box>
          <Box component="p" sx={sectionTitleSx}>
            수신 부서
          </Box>
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: 1,
              border: '1px solid #EFEFEF',
              p: 1.25,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            {departments.map((dep, idx) => (
              <Box
                key={dep.depCd ?? idx}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '14px',
                }}
              >
                <Box component="span" sx={{ color: '#918A8A', fontSize: '13px' }}>
                  부서
                </Box>
                <Box component="span" sx={{ fontWeight: 600, color: '#3A3A3A' }}>
                  {dep.depNmLast}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {list.length > 0 && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#3A3A3A',
              }}
            >
              <Icon.Groups width={20} height={20} color="#33ABD1" />
              수신자 ({list.length}명)
            </Box>
            {grouped.length > 1 && (
              <button
                type="button"
                onClick={handleToggleAll}
                style={{
                  all: 'unset',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#33ABD1',
                }}
              >
                {isAllExpanded ? '전체 접기' : '전체 펼치기'}
              </button>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {grouped.map((group) => (
              <Accordion
                key={group.depNmLast}
                expanded={expandedDeps.has(group.depNmLast)}
                onChange={handleToggleDep(group.depNmLast)}
                disableGutters
                sx={accordionSx}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon sx={{ fontSize: 20, color: '#918A8A' }} />
                  }
                  sx={{
                    minHeight: 44,
                    px: 1.25,
                    '& .MuiAccordionSummary-content': {
                      my: 0.75,
                      alignItems: 'center',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      pr: 0.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ fontSize: '14px', fontWeight: 700, color: '#3A3A3A' }}
                    >
                      {group.depNmLast}
                    </Box>
                    <Box
                      component="span"
                      sx={{ fontSize: '13px', color: '#918A8A', flexShrink: 0 }}
                    >
                      {group.members.length}명
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1 }}>
                  {group.members.map((person, idx) => (
                    <RecipientMemberRow
                      key={person.id ?? `${person.name}-${idx}`}
                      person={person}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
